import { useAuth } from '@/auth/auth-provider'
import { isSuper, parsePrincipalFromUser } from '@/auth/principal'
import { ConfigFormHeader, ConfigViewOnlyContext } from '@/components/ConfigFormHeader'
import { useDealership } from '@/contexts/DealershipContext'
import { getGroupConfiguration, updateGroupConfiguration } from '@/services/configuration.api'
import { getApiErrorMessage } from '@/utils/apiError'
import { buildGroupConfigurationPayload } from '@/utils/groupConfigurationPayload'
import { Card, Group, LoadingOverlay, Stack, Text, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { GroupBusinessBasicsForm } from './GroupBusinessBasicsForm'

interface GroupBusinessBasicsData {
  groupName: string
  groupDescription?: string
  headquarters?: {
    address?: {
      line1?: string
      line2?: string
      city?: string
      state?: string
      zipCode?: string
    }
    primaryPhone?: string
    email?: string
    website?: string
  }
  brandingGuidelines?: {
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
  }
  timezone?: string
  corporateContacts?: Array<{
    name: string
    email: string
    phone: string
    title: string
  }>
}

export function GroupConfiguration() {
  const location = useLocation()
  const { user } = useAuth()
  const principal = useMemo(() => parsePrincipalFromUser(user), [user])
  // Staff see group configuration but cannot change it; the API refuses their writes as well.
  const isViewOnly = !isSuper(principal)
  const { selectedEntity, findEntityById, setSelectedEntity } = useDealership()
  const [configData, setConfigData] = useState<GroupBusinessBasicsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [formMethods, setFormMethods] = useState<{ reset: () => void; submit: () => void } | null>(null)

  // Prefer groupId from navigation state (e.g. after creating a new group) so we show the correct context
  const stateGroupId = (location.state as { groupId?: string } | null)?.groupId
  const effectiveGroupId = stateGroupId ?? (selectedEntity?.type === 'group' ? selectedEntity.id : null)
  const displayName =
    selectedEntity?.type === 'group' && selectedEntity.id === effectiveGroupId
      ? selectedEntity.name
      : (configData?.groupName ?? effectiveGroupId ?? 'Group')

  // When we landed with state.groupId and context now has that group, sync selection so sidebar/context stay correct
  useEffect(() => {
    if (!stateGroupId) return
    const entity = findEntityById(stateGroupId, 'group')
    if (entity) setSelectedEntity(entity)
  }, [stateGroupId, findEntityById, setSelectedEntity])

  // Load configuration
  const loadConfiguration = async () => {
    if (!effectiveGroupId) return
    setIsLoading(true)
    try {
      const response = await getGroupConfiguration<GroupBusinessBasicsData>(effectiveGroupId)
      if (response.success && response.data) {
        setConfigData(response.data)
      } else {
        // Set default values if no data
        setConfigData({
          groupName: displayName,
          groupDescription: '',
          headquarters: {
            address: {
              line1: '',
              line2: '',
              city: '',
              state: '',
              zipCode: '',
            },
            primaryPhone: '',
            email: '',
            website: '',
          },
          brandingGuidelines: {
            logoUrl: '',
            primaryColor: '#003087',
            secondaryColor: '#FFB81C',
          },
          timezone: 'America/Chicago',
          corporateContacts: [],
        })
      }
    } catch (error) {
      console.error('Failed to load group configuration:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to load group configuration',
        color: 'red',
        icon: <IconX size={16} />,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Load configuration when effective group changes
  useEffect(() => {
    loadConfiguration()
  }, [effectiveGroupId])

  // Handle save
  const handleSave = async (data: GroupBusinessBasicsData) => {
    if (!effectiveGroupId || isViewOnly) return
    const groupId = effectiveGroupId
    setIsSaving(true)
    try {
      const payload = buildGroupConfigurationPayload(data)
      const response = await updateGroupConfiguration(groupId, payload)
      if (response.success) {
        notifications.show({
          title: 'Success',
          message: 'Group configuration saved successfully',
          color: 'green',
          icon: <IconCheck size={16} />,
        })
        setConfigData(response.data ?? data)
      } else {
        notifications.show({
          title: 'Error',
          message: response.error || 'Failed to save group configuration',
          color: 'red',
          icon: <IconX size={16} />,
        })
      }
    } catch (error: any) {
      const message = error?.response?.data
        ? getApiErrorMessage(error, 'Failed to save group configuration')
        : (error?.message ?? 'An unexpected error occurred')
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
        icon: <IconX size={16} />,
        autoClose: 10000,
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Guard check after all hooks
  if (!effectiveGroupId) {
    return <Text>Please select a dealership group</Text>
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>Group Configuration</Title>
          <Text size="sm" c="dimmed">
            Configure group-wide settings and business basics for {displayName}
          </Text>
        </div>
        <ConfigViewOnlyContext.Provider value={isViewOnly}>
          <ConfigFormHeader onReset={() => formMethods?.reset()} onSave={() => formMethods?.submit()} isSaving={isSaving} />
        </ConfigViewOnlyContext.Provider>
      </Group>


      {isLoading ? (
        <Card shadow="sm" padding="xl" radius="md" withBorder style={{ position: 'relative', minHeight: 400 }}>
          <LoadingOverlay visible={true} />
        </Card>
      ) : (
        // Disables every input in the form, as on the dealership configuration screen.
        <fieldset disabled={isViewOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <GroupBusinessBasicsForm data={configData} onSave={handleSave} onFormReady={setFormMethods} />
        </fieldset>
      )}
    </Stack>
  )
}
